<template>
  <vs-wrapper>
    <vs-form ref="form">
      <vs-text-input
        label="Name"
        :value="name"
        required
        data-test="name-field"
        autofocus
        @input="name = $event"
        @blur="handleNameBlur"
      />

      <vs-text-input
        label="Short code"
        :value="shortCode"
        :max-length="16"
        required
        data-test="short-code-field"
        :disabled="isLoadingShortNameSuggestion"
        @input="shortCode = $event"
      />

      <vs-button
        type="secondary"
        label="Suggest available short code"
        class="shift-up"
        small
        data-test="short-code-suggestion-button"
        :loading="isLoadingShortNameSuggestion"
        :disabled="(name?.length ?? 0) === 0"
        @click.stop="handleSuggestShortCode"
      />

      <vs-text-input
        label="Billing address"
        data-test="billing-address-field"
        multiline
        :value="billingAddress"
        @input="billingAddress = $event"
      />
    </vs-form>

    <v-row no-gutters justify="end" align="center" class="mt-3">
      <vs-button
        type="secondary"
        label="Save and return"
        data-test="save-and-return-button"
        :loading="creatingClient"
        class="mr-4"
        @click.stop="submit(false)"
      />
      <vs-button
        type="primary"
        label="Save and create job"
        data-test="save-and-create-job-button"
        :loading="creatingClient"
        @click.stop="submit(true)"
      />
    </v-row>
  </vs-wrapper>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import { AxiosError } from 'axios'
import { getShortCodeSuggestion } from '@/api/companies'
import { useJobFilterStore } from '@/components/field-view-next/JobsFilterStore'
import { useCreateClientMutation } from '@/api/account-data/manage-companies'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsButton from '@/components/vision/VsButton.vue'
import VsForm from '@/components/vision/VsForm.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'

const props = defineProps<{
  initialClientName: string
}>()

const emit = defineEmits<{
  (
    e: 'create-client-success',
    data: {
      clientHubId: string
      clientHubName: string
      createNewJob: boolean
    },
  )
  (e: 'create-client-failure', error: unknown)
  (e: 'discard')
}>()

const name = ref<string | null>()
const shortCode = ref<string | null>()
const billingAddress = ref<string | null>()

const isLoadingShortNameSuggestion = ref<boolean>(false)

const filterStore = useJobFilterStore()

const { mutateAsync: createClient, isLoading: creatingClient } = useCreateClientMutation()

watch(
  () => props.initialClientName,
  (newVal) => {
    name.value = newVal
  },
  { immediate: true },
)

const handleNameBlur = () => {
  if (name.value && !shortCode.value) {
    handleSuggestShortCode()
  }
}

const handleSuggestShortCode = async () => {
  isLoadingShortNameSuggestion.value = true
  setTimeout(async () => {
    try {
      const res = await getShortCodeSuggestion(name.value)
      shortCode.value = res.shortCode ?? null
    } catch (e) {
    } finally {
      isLoadingShortNameSuggestion.value = false
    }
  }, 300)
}

// Submission
const form = ref()
const store = useStore()

const submit = async (navigateToNewJobPage: boolean) => {
  if (!form.value.isValid()) {
    return
  }
  try {
    const response = await createClient({
      name: name.value ?? '',
      shortCode: shortCode.value ?? '',
      billingAddress: billingAddress.value,
    })
    const clientOrgId = response.data?.organization?.id
    const clientHubId = response.data?.hub?.id

    if (!clientOrgId || !clientHubId) {
      emit('create-client-failure', 'Unknown error occurred')
      return
    }

    await Promise.all([
      store.dispatch('fetchJobData', true),
      store.dispatch('permissions/fetchCurrentUserContext'),
    ])
    updateDivisionFilters(clientHubId)
    emit('create-client-success', {
      clientHubId,
      clientHubName: response.data?.organization?.name ?? '',
      createNewJob: navigateToNewJobPage,
    })
  } catch (err: unknown) {
    setError(err)
  }
}

const updateDivisionFilters = (newHubId: string) => {
  const currentFilters = filterStore.selectedDivisions
  const newDivId = store.getters['divisions/getDivisionsForHub'](newHubId)?.[0]?.uuid

  if (newDivId && currentFilters.length > 0) {
    filterStore.updateDivision([...currentFilters, newDivId])
  }
}

const setError = (err: any) => {
  let extractedErr: string | null = null
  try {
    switch (err?.constructor) {
      case AxiosError:
        extractedErr = Object.values(err?.response?.data?.errors).join(' ')
        break
      case Error:
        extractedErr = err?.message
    }
  } finally {
    emit('create-client-failure', extractedErr || 'Unknown error occurred')
  }
}
</script>

<style scoped>
.shift-up {
  margin-top: calc(var(--space-base) * -1);
}
</style>
